import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Settings, Eye, EyeOff, Loader2, Check, User, BadgeCheck } from "lucide-react";
import InvestorPortalDisclaimer from "@/components/investor-portal-disclaimer";

const BASE = import.meta.env.BASE_URL;

export default function InvestorPortalSettings() {
  const [, navigate] = useLocation();
  const [investor, setInvestor] = useState<any>(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const data = localStorage.getItem("investor_data");
    if (data) {
      try { setInvestor(JSON.parse(data)); } catch {}
    }
    const token = localStorage.getItem("investor_token");
    if (!token) navigate("/investor/login");
  }, [navigate]);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (newPassword !== confirmPassword) {
      setError("New passwords don't match");
      return;
    }
    if (newPassword.length < 6) {
      setError("New password must be at least 6 characters");
      return;
    }

    setLoading(true);
    const token = localStorage.getItem("investor_token");

    try {
      const res = await fetch(`${BASE}api/investor-portal/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-investor-token": token || "" },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      if (res.ok) {
        setSuccess(true);
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setTimeout(() => setSuccess(false), 3000);
      } else {
        const err = await res.json();
        setError(err.error || "Failed to change password");
      }
    } catch {
      setError("Connection error");
    }
    setLoading(false);
  };

  return (
    <div className="space-y-8 max-w-lg">
      <div>
        <h1 className="text-2xl font-bold font-serif tracking-tight text-white flex items-center gap-2">
          <Settings className="w-6 h-6 text-[#FF5C00]" /> Settings
        </h1>
        <p className="text-white/40 text-sm mt-1">Manage your account</p>
      </div>

      {investor && (
        <div className="bg-white/[0.03] border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-4 h-4 text-white/40" />
            <p className="text-white/40 text-xs uppercase tracking-wider font-medium">Profile</p>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-white/50">Name</span>
              <span className="text-white font-medium">{investor.name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-white/50">Email</span>
              <span className="text-white font-medium">{investor.email}</span>
            </div>
            {investor.phone && (
              <div className="flex justify-between text-sm">
                <span className="text-white/50">Phone</span>
                <span className="text-white font-medium">{investor.phone}</span>
              </div>
            )}
            {investor.firm && (
              <div className="flex justify-between text-sm">
                <span className="text-white/50">Firm</span>
                <span className="text-white font-medium">{investor.firm}</span>
              </div>
            )}
            {investor.selectProduct && (
              <div className="flex justify-between items-center text-sm">
                <span className="text-white/50">Select Product</span>
                <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-0.5 rounded-full border ${
                  investor.selectProduct === "blind_pool"
                    ? "bg-purple-500/10 border-purple-500/20 text-purple-400"
                    : "bg-cyan-500/10 border-cyan-500/20 text-cyan-400"
                }`}>
                  <BadgeCheck className="w-3 h-3" />
                  {investor.selectProduct === "blind_pool" ? "Shubh Labh Pool" : "Pre-Screening"}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bg-white/[0.03] border border-white/10 rounded-xl p-6">
        <p className="text-white/80 text-sm font-medium mb-4">Change Password</p>

        {success && (
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 mb-4 flex items-center gap-2 text-emerald-400 text-sm">
            <Check className="w-4 h-4" /> Password changed successfully
          </div>
        )}

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4 text-red-400 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label className="block text-white/40 text-xs uppercase tracking-wider mb-1.5">Current Password</label>
            <div className="relative">
              <input
                type={showCurrent ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#FF5C00]/50"
                required
              />
              <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30">
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-white/40 text-xs uppercase tracking-wider mb-1.5">New Password</label>
            <div className="relative">
              <input
                type={showNew ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#FF5C00]/50"
                required
                minLength={6}
              />
              <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30">
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-white/40 text-xs uppercase tracking-wider mb-1.5">Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#FF5C00]/50"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="bg-[#FF5C00] hover:bg-[#E55200] text-white px-5 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2 transition-colors"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Change Password
          </button>
        </form>
      </div>

      <InvestorPortalDisclaimer />
    </div>
  );
}
